//dependencies
import React, { useEffect, useRef, useState } from "react";

import { toast } from "@performyard/py-components";

//components
import Button from "../../../../../../../components/Button";
import Container from "../../../../../../../components/Container";
import { Divider } from "../../../../../../../components/Divider";
import RangeInput from "../../../../../../../components/Inputs/RangeInput";
import Spacer from "../../../../../../../components/Spacer";
import Typography from "../../../../../../../components/Text";

//hooks
import { useMutateEmployeeAvatar } from "../../../../../../../api/hooks";

//utils
import { cropImageFromPixelCropAndHTMLImage } from "../../../../../../../utils/CropImage";

//css
import css from "../../AvatarModal.module.scss";

const MIN_DEMENSIONS = 300;

export type EditAvatarModalContentProps = {
	handleClose: () => void;
	userID: string;
	file: File;
	setFile: (file: File | null) => void;
};

const EditAvatarModalContents = ({ handleClose, userID, file, setFile }: EditAvatarModalContentProps) => {
	const imgRef = useRef<HTMLImageElement>(null);
	const cropperRef = useRef<HTMLDivElement>(null);
	const [imageSrc, setImageSrc] = useState<string>("");
	const [scale, setScale] = useState(1);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const [startingPosition, setStartingPosition] = useState({ x: 0, y: 0 });
	const cropSize = cropperRef.current?.getBoundingClientRect().width
		? Math.min(cropperRef.current?.getBoundingClientRect().width, MIN_DEMENSIONS)
		: MIN_DEMENSIONS;

	useEffect(() => {
		if (file) {
			setImageSrc(URL.createObjectURL(file));
		}

		return () => {
			URL.revokeObjectURL(imageSrc);
		};
	}, [file]);

	const { mutate: editAvatar, isPending: isEditAvatarPending } = useMutateEmployeeAvatar({
		onSuccess: () => {
			toast.success(`Avatar Updated`);

			handleClose();
		},
		meta: {
			errorMessage: "There was a problem updating the employee avatar",
		},
	});

	const completeCropAndSave = async () => {
		if (imgRef.current) {
			//when cropping, we need to override the crop x and y with the position x and y, but also need to account for the default centering of the crop
			const imageWidth = imgRef.current.width;
			const imgHeight = imgRef.current.height;
			const x = position.x * -1 + (imageWidth / 2 - cropSize / 2);
			const y = position.y * -1 + (imgHeight / 2 - cropSize / 2);
			const file = await cropImageFromPixelCropAndHTMLImage(
				imgRef?.current,
				{
					width: cropSize,
					height: cropSize,
					x,
					y,
				},
				scale
			);
			if (file) {
				editAvatar({ employeeId: userID, file: file });
			}
		}
	};

	const startDrag = (clientX: number = 0, clientY: number = 0) => {
		setIsDragging(true);
		const { x = 0, y = 0 } = position;

		setStartingPosition({ x: clientX - x, y: clientY - y });
	};

	const handleDrag = (clientX: number = 0, clientY: number = 0) => {
		if (isDragging) {
			const { x = 0, y = 0 } = startingPosition;
			setPosition({ x: clientX - x, y: clientY - y });
		}
	};

	const endDrag = () => {
		setIsDragging(false);
	};

	return (
		<Container column centerVertical center className={`${css.contents} ${css.edit}`}>
			<div
				ref={cropperRef}
				onMouseMove={(e) => handleDrag(e.clientX, e.clientY)}
				onMouseLeave={endDrag}
				onMouseUp={endDrag}
				onTouchMove={(e) => e.touches[0] && handleDrag(e.touches[0].clientX, e.touches[0].clientY)}
				onTouchEnd={endDrag}
				onTouchCancel={endDrag}
				className={css.cropper}
				style={{ maxHeight: cropSize }}
			>
				<img
					src={imageSrc}
					draggable={false}
					alt="uploaded avatar"
					height={cropSize}
					className={css.croppedImage}
					style={{
						cursor: isDragging ? "grabbing" : "grab",
						transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
					}}
					ref={imgRef}
					onMouseDown={(e) => {
						e.preventDefault();
						startDrag(e.clientX, e.clientY);
					}}
					onTouchStart={(e) => {
						e.preventDefault();
						e.touches[0] && startDrag(e.touches[0].clientX, e.touches[0].clientY);
					}}
				/>
				<div
					className={css.circleCropOverlay}
					style={{
						width: cropSize,
						height: cropSize,
						position: "absolute",
						left: `calc(50% - ${cropSize / 2})`,
						pointerEvents: "none",
					}}
				/>
			</div>
			<Spacer height={2} />
			<RangeInput
				min={1}
				max={3}
				step={0.1}
				value={scale}
				onChange={(value) => setScale(value)}
				className={css.rangeInput}
			/>
			<Spacer height={2} />
			<Typography size="sub-heading" weight="semi-bold">
				Edit Photo
			</Typography>
			<Typography size="paragraph-3" weight="regular">
				It may take a few hours for your new photo to be displayed
			</Typography>
			<Spacer height={4} />
			<Divider />
			<Spacer height={2} />
			<Container horizontalAlignment="space-around" gap="8px">
				<Button color="red" onClick={() => setFile(null)}>
					Cancel
				</Button>
				<Button color="green" isRequesting={isEditAvatarPending} onClick={completeCropAndSave}>
					Save
				</Button>
			</Container>
		</Container>
	);
};

export default EditAvatarModalContents;
